<template>
  <b-modal
    id="modal-hanwha-logs-filters"
    @show="onShow"
    centered
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    scrollable
    size="lg"
  >
    <div>
      <b-row align-v="center" class="d-flex align-items-center">
        <b-col cols="12" class="mb-2">
          <label class="text-capitalize d-block mb-0">
            {{ $t("hanwha_log.dvr") }}
          </label>
          <b-form-checkbox v-model="autoDetectDvr" class="mb-50" switch>
            <span class="text-capitalize small">{{ $t("hanwha_log.autoDetectDvr") }}</span>
          </b-form-checkbox>
          <v-select
            v-model="dvrFilter"
            :options="dvrOptions"
            :reduce="(val) => val.value"
            :disabled="autoDetectDvr"
            :close-on-select="false"
            :clear-search-on-select="false"
            :hide-selected="false"
            multiple
            class="w-100"
            append-to-body
            :calculate-position="withPopper"
          >
            <template #option="option">
              <div class="dvr-option d-flex align-items-center">
                <span
                  class="custom-option-checkbox mr-50"
                  :class="{ 'custom-option-checkbox-selected': dvrFilter.includes(option.value) }"
                >
                  <feather-icon
                    v-if="dvrFilter.includes(option.value)"
                    icon="CheckIcon"
                    size="13"
                  />
                </span>
                <span>{{ option.label }}</span>
              </div>
            </template>
          </v-select>
          <small v-if="autoDetectDvr" class="text-muted">
            {{ $t("hanwha_log.autoDetectDvrHint") }}
          </small>
        </b-col>

        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $t("hanwha_log.typeLog") }}
          </label>
          <v-select
            v-model="typeLogFilter"
            :options="typeLogOptions"
            :reduce="(val) => val.value"
            multiple
            class="w-100"
            append-to-body
            :calculate-position="withPopper"
          />
        </b-col>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $t("hanwha_log.typeEvent") }}
          </label>
          <v-select
            v-model="typeFilter"
            :options="typeOptions"
            :reduce="(val) => val.value"
            multiple
            :disabled="typeOptions.length === 0"
            class="w-100"
            append-to-body
            :calculate-position="withPopper"
          />
        </b-col>

        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">{{
            $t("alarm.start_date_filter")
          }}</label>
          <flat-pickr
            v-model="startDatetime"
            class="form-control"
            :disabled="!!dateRangeFilter"
            :config="{
              enableTime: true,
              dateFormat: 'Y-m-d H:i:s',
              time_24hr: true,
            }"
            :value="startDatetime"
          />
        </b-col>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">{{
            $t("alarm.end_date_filter")
          }}</label>
          <flat-pickr
            v-model="endDatetime"
            class="form-control"
            :disabled="!!dateRangeFilter"
            :config="{
              enableTime: true,
              dateFormat: 'Y-m-d H:i:s',
              time_24hr: true,
            }"
            :value="endDatetime"
          />
        </b-col>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $t("hanwha_log.dateRange") }}
          </label>
          <v-select
            v-model="dateRangeFilter"
            :options="[
              { label: $t('hanwha_log.dateRangeNone'), value: null },
              { label: $t('hanwha_log.dateRangeLast30'), value: 30 },
              { label: $t('hanwha_log.dateRangeLast60'), value: 60 },
            ]"
            :multiple="false"
            :clearable="false"
            class="w-100"
            append-to-body
            :calculate-position="withPopper"
            :reduce="(val) => val.value"
          />
          <small v-if="dateRangeFilter" class="text-muted">
            {{ $t("hanwha_log.dateRangeHint") }}
          </small>
        </b-col>
      </b-row>
    </div>
    <template #modal-footer>
      <b-button
        size="sm"
        variant="secondary"
        class="text-capitalize"
        @click="$bvModal.hide('modal-hanwha-logs-filters')"
      >
        {{ $t("actions.cancel") }}
      </b-button>
      <b-button
        variant="info"
        size="sm"
        class="text-capitalize"
        @click="applyFilters"
      >
        {{ $t("actions.accept") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import popperHelper from "@/libs/popper-helper";
import { HANWHA_LOG_TYPE_OPTIONS, HANWHA_EVENT_TYPES_BY_LOG } from "./catalogs";

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BFormCheckbox,
    vSelect,
    flatPickr,
  },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dvrFilter: [],
      autoDetectDvr: false,
      typeLogFilter: [],
      typeFilter: [],
      startDatetime: "",
      endDatetime: "",
      dateRangeFilter: null,
    };
  },
  computed: {
    dvrOptions() {
      return this.$store.getters["hanwha_dvr/getSelectedItems"];
    },
    typeLogOptions() {
      return HANWHA_LOG_TYPE_OPTIONS;
    },
    typeOptions() {
      const options = (this.typeLogFilter || [])
        .flatMap((typeLog) => HANWHA_EVENT_TYPES_BY_LOG[typeLog] || [])
        .map((type) => ({ label: type, value: type }));

      return options.filter(
        (option, index, all) => all.findIndex((item) => item.value === option.value) === index
      );
    },
    filtersText() {
      const parts = [];
      if (this.autoDetectDvr) {
        parts.push(this.$t("hanwha_log.autoDetectDvr"));
      } else if (this.dvrFilter?.length) {
        parts.push(this.$t("hanwha_log.dvr"));
      }
      if (this.typeLogFilter?.length) {
        parts.push(this.$t("hanwha_log.typeLog"));
      }
      if (this.typeFilter?.length) {
        parts.push(this.$t("hanwha_log.typeEvent"));
      }
      if (this.dateRangeFilter) {
        parts.push(
          this.dateRangeFilter === 30
            ? this.$t("hanwha_log.dateRangeLast30")
            : this.$t("hanwha_log.dateRangeLast60")
        );
      } else {
        if (this.startDatetime) {
          parts.push(this.$t("alarm.start_date_filter"));
        }
        if (this.endDatetime) {
          parts.push(this.$t("alarm.end_date_filter"));
        }
      }
      return parts.length ? parts.join("<br>") : "";
    },
  },
  methods: {
    async onShow() {
      await this.$store.dispatch("hanwha_dvr/getAll", { max: 1000 });

      if (this.filters && Object.keys(this.filters).length) {
        this.dvrFilter = this.toArray(this.filters.dvrFilter);
        this.autoDetectDvr = !!this.filters.autoDetectDvr;
        this.typeLogFilter = this.filters.typeLogFilter || [];
        this.typeFilter = this.filters.typeFilter || [];
        this.startDatetime = this.filters.startDatetime || "";
        this.endDatetime = this.filters.endDatetime || "";
        this.dateRangeFilter = this.filters.dateRangeFilter || null;
      } else {
        this.dvrFilter = [];
        this.autoDetectDvr = false;
        this.typeLogFilter = [];
        this.typeFilter = [];
        this.startDatetime = "";
        this.endDatetime = "";
        this.dateRangeFilter = null;
      }
    },
    toArray(value) {
      if (Array.isArray(value)) return value;
      return value !== undefined && value !== null && value !== "" ? [value] : [];
    },
    applyFilters() {
      const payload = this.updateFilters();
      this.$emit("filters-applied", payload);
      this.$bvModal.hide("modal-hanwha-logs-filters");
    },
    updateFilters() {
      const filters = {
        dvrFilter: this.dvrFilter,
        autoDetectDvr: this.autoDetectDvr,
        typeLogFilter: this.typeLogFilter,
        typeFilter: this.typeFilter,
        startDatetime: this.startDatetime,
        endDatetime: this.endDatetime,
        dateRangeFilter: this.dateRangeFilter,
      };
      const payload = {
        filters,
        filtersText: this.filtersText,
        filtersCount: this.countActiveFilters(filters),
      };
      this.$emit("filters-selected", payload);
      return payload;
    },
    countActiveFilters(filters) {
      if (!filters) return 0;
      let count = 0;
      for (const key in filters) {
        const value = filters[key];
        if (key === "autoDetectDvr") {
          if (value) count++;
        } else if (Array.isArray(value)) {
          if (value.length > 0) count++;
        } else if (value !== null && value !== "" && value !== undefined) {
          count++;
        }
      }
      return count;
    },
  },
  setup() {
    return {
      withPopper: popperHelper.withPopper,
    };
  },
  watch: {
    typeLogFilter() {
      const validTypes = new Set(this.typeOptions.map((option) => option.value));
      this.typeFilter = this.typeFilter.filter((type) => validTypes.has(type));
    },
    filters: {
      immediate: true,
      deep: true,
      handler(newFilters, oldFilters) {
        if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
          this.dvrFilter = this.toArray(newFilters.dvrFilter);
          this.autoDetectDvr = !!newFilters.autoDetectDvr;
          this.typeLogFilter = newFilters.typeLogFilter || [];
          this.typeFilter = newFilters.typeFilter || [];
          this.startDatetime = newFilters.startDatetime || "";
          this.endDatetime = newFilters.endDatetime || "";
          this.dateRangeFilter = newFilters.dateRangeFilter || null;
          this.updateFilters();
        }
      },
    },
  },
};
</script>

<style lang="scss">
.dvr-option {
  display: flex;
  width: 100%;
  min-height: 28px;
  align-items: center;
  cursor: pointer;
}

.custom-option-checkbox {
  display: inline-flex;
  width: 17px;
  height: 17px;
  flex: 0 0 17px;
  align-items: center;
  justify-content: center;
  border: 1px solid #b9b9c3;
  border-radius: 3px;
  background-color: #fff;
  pointer-events: none;
}

.custom-option-checkbox-selected {
  color: #fff;
  border-color: #7367f0;
  background-color: #7367f0;
}

.vs__dropdown-option .dvr-option {
  width: 100%;
}
</style>
